import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface ElevatorPitchProps {
  initialWho?: string;
  initialProblem?: string;
  initialSolution?: string;
  initialWhy?: string;
  onSaveWho: (value: string) => void;
  onSaveProblem: (value: string) => void;
  onSaveSolution: (value: string) => void;
  onSaveWhy: (value: string) => void;
}

export default function ElevatorPitch({
  initialWho = '',
  initialProblem = '',
  initialSolution = '',
  initialWhy = '',
  onSaveWho,
  onSaveProblem,
  onSaveSolution,
  onSaveWhy,
}: ElevatorPitchProps) {
  const [who, setWho] = useState(initialWho);
  const [problem, setProblem] = useState(initialProblem);
  const [solution, setSolution] = useState(initialSolution);
  const [why, setWhy] = useState(initialWhy);

  useEffect(() => { setWho(initialWho); }, [initialWho]);
  useEffect(() => { setProblem(initialProblem); }, [initialProblem]);
  useEffect(() => { setSolution(initialSolution); }, [initialSolution]);
  useEffect(() => { setWhy(initialWhy); }, [initialWhy]);

  const hasContent = who || problem || solution || why;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto max-w-md"
    >
      {/* Intro */}
      <div className="rounded-2xl bg-gradient-to-br from-purple-50 to-brand-50 p-5 text-center">
        <span className="text-4xl">🎤</span>
        <h2 className="mt-2 text-2xl font-bold text-gray-900">Elevator Pitch</h2>
        <p className="mt-2 text-sm text-gray-600">
          Picture this: you&apos;re in an elevator with an investor or a famous YouTuber…
          You&apos;ve only got 30 seconds. Go!
        </p>
      </div>

      {/* Fields */}
      <div className="mt-5 space-y-4">
        <div>
          <label className="text-sm font-bold text-gray-800">
            1. Who are you?
          </label>
          <input
            type="text"
            value={who}
            onChange={(e) => {
              setWho(e.target.value);
              onSaveWho(e.target.value);
            }}
            placeholder="I'm a 12-year-old inventor who..."
            className="mt-1 w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-sm font-bold text-gray-800">
            2. The problem you&apos;re solving
          </label>
          <input
            type="text"
            value={problem}
            onChange={(e) => {
              setProblem(e.target.value);
              onSaveProblem(e.target.value);
            }}
            placeholder="Kids always struggle with..."
            className="mt-1 w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-sm font-bold text-gray-800">
            3. The awesome solution you&apos;ve created
          </label>
          <input
            type="text"
            value={solution}
            onChange={(e) => {
              setSolution(e.target.value);
              onSaveSolution(e.target.value);
            }}
            placeholder="My product makes it super easy to..."
            className="mt-1 w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="text-sm font-bold text-gray-800">
            4. Why people should care right now
          </label>
          <input
            type="text"
            value={why}
            onChange={(e) => {
              setWhy(e.target.value);
              onSaveWhy(e.target.value);
            }}
            placeholder="Because right now, everyone needs..."
            className="mt-1 w-full rounded-xl border-2 border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-300 focus:border-brand-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Live preview */}
      {hasContent && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-6 rounded-2xl border-2 border-purple-200 bg-purple-50 p-5"
        >
          <h3 className="mb-2 text-sm font-bold text-purple-700">Your Pitch Preview</h3>
          <p className="text-sm leading-relaxed text-gray-800">
            {who || '______'}.{' '}
            {problem || '______'}.{' '}
            {solution || '______'}.{' '}
            {why || '______'}.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}
